import { getDefaultValueForType } from "./derivedLists";
import { resolveTypeKey, semanticToVisibleType } from "./resolve";
import {
  coerceDateOnly,
  coerceDateTimeLocal,
  coerceString,
  coerceTimeOnly,
  normalizeCurrencyObject,
  parseUrl,
  sanitizeCurrencyForApi,
} from "./parsers";

const generateId = () =>
  `field_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

/**
 * Dropdown/combo values are sometimes stored as `{ options, value }` instead of a scalar.
 * Unwrap so React never tries to render that object as a child.
 */
export const unwrapEnumLikeValue = (rawValue, fallbackOptions = []) => {
  let options = Array.isArray(fallbackOptions) ? [...fallbackOptions] : [];
  let current = rawValue;

  while (current && typeof current === "object" && !Array.isArray(current)) {
    if (Array.isArray(current.options) && current.options.length) {
      options = current.options;
    }
    if (!Object.prototype.hasOwnProperty.call(current, "value")) {
      break;
    }
    current = current.value;
  }

  const value =
    current === undefined || current === null || current === ""
      ? null
      : typeof current === "object"
        ? null
        : String(current);

  return { value, options };
};

export const getEnumDisplayValue = (rawValue) =>
  unwrapEnumLikeValue(rawValue).value || "";

/**
 * Normalize inbound API/props data into editor field shape.
 */
export const normalizeCustomField = (input = {}) => {
  const inferredType = resolveTypeKey(
    input.type || input.visibleType || input._class
  );
  const typeKey = resolveTypeKey(inferredType);

  const name = coerceString(input.name).trim();
  const normalized = {
    id: input.id || (name ? `field_${name}` : generateId()),
    name,
    type: typeKey,
    visibleType: input.visibleType,
    value:
      input.value !== undefined ? input.value : getDefaultValueForType(typeKey),
    startDate:
      input.startDate ??
      input.value?.startDate ??
      input.value?.startDateTime ??
      input.start ??
      null,
    endDate:
      input.endDate ??
      input.value?.endDate ??
      input.value?.endDateTime ??
      input.end ??
      null,
    options: Array.isArray(input.options)
      ? input.options
      : Array.isArray(input.value?.options)
      ? input.value.options
      : [],
    editable: input.editable === undefined ? true : Boolean(input.editable),
  };

  if (!normalized.visibleType) {
    normalized.visibleType = semanticToVisibleType(typeKey);
  }

  switch (typeKey) {
    case "DATE":
      normalized.value = coerceDateOnly(normalized.value);
      break;
    case "TIME":
      normalized.value = coerceTimeOnly(normalized.value);
      break;
    case "DATETIME":
      normalized.value = coerceDateTimeLocal(normalized.value);
      break;
    case "URL":
      normalized.value = coerceString(normalized.value).trim();
      break;
    case "CURRENCY":
      normalized.value = normalizeCurrencyObject(normalized.value);
      break;
    case "STRING_ARRAY":
      normalized.value = Array.isArray(normalized.value)
        ? normalized.value.map((item) => coerceString(item).trim()).filter(Boolean)
        : [];
      break;
    case "PERIOD":
      normalized.startDate = coerceDateOnly(normalized.startDate);
      normalized.endDate = coerceDateOnly(normalized.endDate);
      break;
    case "ENUM":
    case "COMBOLIST": {
      const unwrapped = unwrapEnumLikeValue(
        normalized.value,
        normalized.options
      );
      normalized.value = unwrapped.value;
      normalized.options = unwrapped.options;
      normalized.startDate = null;
      normalized.endDate = null;
      break;
    }
    default:
      normalized.startDate = null;
      normalized.endDate = null;
  }

  return normalized;
};

/**
 * Normalize a field value for entity/template save paths (API-bound).
 */
export const normalizeFieldValueForSave = (field) => {
  const type = resolveTypeKey(field?.type);
  const rawValue = field?.value;

  if (type === "PERIOD") {
    const rawStartDate =
      field?.startDate ?? rawValue?.startDate ?? rawValue?.startDateTime ?? null;
    const rawEndDate =
      field?.endDate ?? rawValue?.endDate ?? rawValue?.endDateTime ?? null;
    return {
      startDate: rawStartDate || null,
      endDate: rawEndDate || null,
    };
  }

  if (rawValue === undefined || rawValue === null || rawValue === "") {
    return getDefaultValueForType(type);
  }

  if (type === "STRING_ARRAY") {
    if (Array.isArray(rawValue)) {
      return rawValue
        .filter((item) => item !== null && item !== undefined && item !== "")
        .map((item) => String(item));
    }
    return [];
  }

  if (type === "URL") {
    const trimmed = String(rawValue).trim();
    if (!trimmed) return "";
    return parseUrl(trimmed);
  }

  if (type === "CURRENCY") {
    return sanitizeCurrencyForApi(rawValue);
  }

  if (type === "ENUM" || type === "COMBOLIST") {
    return unwrapEnumLikeValue(rawValue, field?.options).value;
  }

  return rawValue;
};

export const normalizeCustomFieldForSave = (field) => {
  if (!field || typeof field !== "object") return null;
  const name = String(field.name || "").trim();
  if (!name) return null;

  const type = resolveTypeKey(field.type);
  const normalized = {
    ...field,
    name,
    type,
    value: normalizeFieldValueForSave(field),
  };

  if (type === "PERIOD") {
    normalized.startDate = normalized.value.startDate;
    normalized.endDate = normalized.value.endDate;
    delete normalized.value.startDateTime;
    delete normalized.value.endDateTime;
  } else {
    delete normalized.startDate;
    delete normalized.endDate;
  }

  if (type === "ENUM" || type === "COMBOLIST") {
    const unwrapped = unwrapEnumLikeValue(field.value, field.options);
    normalized.options = unwrapped.options;
    normalized.value = unwrapped.value;
  }

  if (type === "CURRENCY" && normalized.value == null) {
    return null;
  }

  return normalized;
};
